import React, { useRef, type ComponentType } from 'react';
import { ReportErrors } from '@jira/platform__error-reporting/src/ReportErrors.tsx';
import { Drawer, DrawerCloseButton, DrawerContent, DrawerSidebar } from '@atlaskit/drawer';
import CloseIcon from '@atlaskit/icon/core/cross';
import { Grid, xcss } from '@atlaskit/primitives';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';

import Placeholder, { PlaceholderNew } from '@atlassian/jira-placeholder/src/index.tsx';
import { monitor } from '@atlassian/jira-portfolio-3-common/src/analytics/performance.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useRouterActions } from '@atlassian/react-resource-router';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	PACKAGE_NAME,
	ERROR_REPORTING_TEAM,
	PERFORMANCE_CREATE_PLAN_WIZARD_LOAD_TIME,
} from '../common/constants.tsx';
import { useCreateNewPlanWizardDrawer } from '../controllers/index.tsx';
import type { Props as PlanWizardProps } from './content/types.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	drawerContent: {
		marginTop: '0',
	},
});

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const AsyncPlanWizard = lazy<ComponentType<PlanWizardProps>>(
	() => {
		monitor.startTask(PERFORMANCE_CREATE_PLAN_WIZARD_LOAD_TIME);
		return import(
			/* webpackChunkName: "async-portfolio-3-create-new-plan-wizard" */ './content'
		).then((module) => module.default);
	},
	{ ssr: false },
);

const AsyncPlanWizardWrapped = (props: PlanWizardProps) => (
	<PlaceholderNew name="async-plan-create-wizard" fallback={null}>
		<AsyncPlanWizard {...props} />
	</PlaceholderNew>
);

export type CreateNewPlanWizardDrawerProps = {
	PlanWizard?: ComponentType<PlanWizardProps>;
};

const CreateNewPlanWizardDrawer = ({
	PlanWizard = fg('concurrent-rendering-fix-plan-create-drawer')
		? AsyncPlanWizardWrapped
		: AsyncPlanWizard,
}: CreateNewPlanWizardDrawerProps): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isOpen, plan, isPlanCreationComplete, hasPlanInvitePermission }, { closeDrawer }] =
		useCreateNewPlanWizardDrawer();
	const keepMounted = useRef(false);
	const { push } = useRouterActions();
	const { formatMessage } = useIntl();

	if (!keepMounted.current && !isOpen) return null;
	// Keep mounted to Drawer close animation works
	if (!keepMounted.current) keepMounted.current = true;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleRequestClose = (e: any) => {
		if (e.key === 'Escape') {
			return;
		}
		closeDrawer();
		if (
			isPlanCreationComplete &&
			plan?.id &&
			hasPlanInvitePermission &&
			expValEquals('pillar_0_exp_2_invite_users_when_creating_plan', 'cohort', 'test', 'control')
		) {
			// Redirect to the newly created plan if the user deliberately closes the drawer after plan created
			push(`/jira/plans/${plan.id}`);
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createNewPlanWizardDrawerClosed',
				attributes: { planId: plan.id },
			});
		}
	};

	// Wrapping the AsyncPlanWizard component directly in Placeholder results the Drawer
	// flickering when it is opened immediately after the /plans page loads - either through
	// the create-plan=true query param, or by quickly clicking the top RHS Create plan
	// button. Wrapping the parent Drawer in Placeholder resolves this issue.

	return (
		<Placeholder name="create-new-plan-wizard" fallback={null}>
			<Drawer
				isOpen={isOpen}
				width="full"
				onClose={handleRequestClose}
				label={formatMessage(messages.dialogLabel)}
			>
				{/*
					With multi-step wizard, we don't want to show the close button in the sidebar
					we are handling the close button in the steps to allow the user skip and go to the plan
				*/}
				{expValEquals('multi-step_plans_setup_wizard', 'cohort', 'test', 'control') ? (
					!isPlanCreationComplete && (
						<DrawerSidebar>
							<DrawerCloseButton icon={(iconsProps) => <CloseIcon {...iconsProps} />} />
						</DrawerSidebar>
					)
				) : (
					<DrawerSidebar>
						<DrawerCloseButton icon={(iconsProps) => <CloseIcon {...iconsProps} />} />
					</DrawerSidebar>
				)}

				<DrawerContent
					{...(expValEquals('multi-step_plans_setup_wizard', 'cohort', 'test', 'control') && {
						xcss: styles.drawerContent,
					})}
				>
					<Grid
						xcss={
							expValEquals('multi-step_plans_setup_wizard', 'cohort', 'test', 'control')
								? containerStylesFull
								: containerStylesCentred
						}
						alignItems="center"
					>
						<ReportErrors
							id={PACKAGE_NAME}
							packageName={PACKAGE_NAME}
							teamName={ERROR_REPORTING_TEAM}
							sendToPrivacyUnsafeSplunk
						>
							<PlanWizard plan={plan} />
						</ReportErrors>
					</Grid>
				</DrawerContent>
			</Drawer>
		</Placeholder>
	);
};

export default CreateNewPlanWizardDrawer;

const containerStylesCentred = xcss({
	minHeight: '100%',
	paddingTop: 'space.300',
	paddingBottom: 'space.300',
});

const containerStylesFull = xcss({
	minHeight: '100%',
});
